import React, { useEffect, useState } from "react";
import { Link, useNavigate } from 'react-router-dom';

function Cart({ accessToken, refreshCart, onAuthError, onClose }) {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [delivery, setDelivery] = useState(0);
  const [shippingMessage, setShippingMessage] = useState("Calculating...");

  useEffect(() => {
    if (!accessToken) { setLoading(false); return; }
    
    if (refreshCart) {
      refreshCart()
        .then(items => {
          setCartItems(items);
          if (items.length > 0) {
            fetchShippingCost();
          } else {
            setDelivery(0);
            setShippingMessage("FREE");
          }
        })
        .catch(() => setCartItems([]))
        .finally(() => setLoading(false));
    }
  }, [accessToken, refreshCart]);

 const fetchShippingCost = () => {
  setShippingMessage("Recalculating...");

  fetch(`${process.env.REACT_APP_API_URL}/api/products/cart/shipping-rate/`, {
    method: 'GET',
    headers: { 
      'Content-Type': 'application/json', 
      Authorization: `Bearer ${accessToken}` 
    },
  })
    .then(res => res.json())
    .then(data => {
      // Safely navigate nested object: data -> shipping_charge -> shipping_charge
      const chargeData = data?.shipping_charge;

      if (chargeData && typeof chargeData.shipping_charge === 'number') {
        setDelivery(chargeData.shipping_charge);
      } else if (data?.message === "No default address found" || chargeData?.status === "missing_pincode") {
        setDelivery(0);
        setShippingMessage("Add address at checkout");
      } else {
        setDelivery(0);
        setShippingMessage("Calculated at checkout");
      }
    })
    .catch((err) => {
      console.error("Shipping calculation error:", err);
      setDelivery(0);
      setShippingMessage("Calculated at checkout");
    });
};

  const displayItems = cartItems;

  const cartPost = (url, productId) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ product: productId }),
    }).then(res => { if (res.status === 401) { onAuthError && onAuthError(); return null; } return res; });

  const handleIncrease = (item) =>
    fetch(`${process.env.REACT_APP_API_URL}/api/products/cart/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ product: item.product, quantity: 1, size: item.size || 'Standard' }),
    }).then(res => {
      if (res.status === 401) { onAuthError && onAuthError(); return; }
      setCartItems(prev => prev.map(i => i.product === item.product
        ? { ...i, quantity: i.quantity + 1, total_price: ((i.quantity + 1) * parseFloat(i.product_detail?.final_price || 0)) }
        : i));
      if (refreshCart) refreshCart();
      fetchShippingCost();
    });

  const handleDecrease = (item) =>
    cartPost(`${process.env.REACT_APP_API_URL}/api/products/cart/decrease/`, item.product).then(res => {
      if (!res) return;
      if (res.status === 204) {
        const updated = prev => prev.filter(i => i.product !== item.product);
        setCartItems(updated);
        if (cartItems.length <= 1) setDelivery(0);
      } else {
        setCartItems(prev => prev.map(i => i.product === item.product
          ? { ...i, quantity: i.quantity - 1, total_price: ((i.quantity - 1) * parseFloat(i.product_detail?.final_price || 0)) }
          : i));
      }
      if (refreshCart) refreshCart();
      fetchShippingCost();
    });

  const handleRemove = (item) =>
    cartPost(`${process.env.REACT_APP_API_URL}/api/products/cart/remove_item/`, item.product).then(res => {
      if (!res) return;
      const updated = cartItems.filter(i => i.product !== item.product);
      setCartItems(updated);
      if (refreshCart) refreshCart();
      if (updated.length > 0) fetchShippingCost(); else { setDelivery(0); setShippingMessage("FREE"); }
    });

  // Base price computations
  const totalOriginalPrice = displayItems.reduce((sum, item) => {
    const originalPrice = parseFloat(item.product_detail?.price || item.product_detail?.final_price || 0);
    return sum + (originalPrice * (item.quantity || 1));
  }, 0);

  const totalPrice = displayItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
  const totalDiscountAmount = totalOriginalPrice - totalPrice;
  const totalDiscountPercent = totalOriginalPrice > 0 
    ? Math.round((totalDiscountAmount / totalOriginalPrice) * 100) 
    : 0;

  // 🌟 DYNAMIC GST CALCULATION: Sum up tax across all cart items based on DB rates
  const totalGstAmount = displayItems.reduce((sum, item) => {
    if (item.total_gst !== undefined) return sum + parseFloat(item.total_gst);
    
    // Fallback calculation using product_type.gst_percentage from serializer
    const rate = parseFloat(item.product_detail?.gst_rate || item.product_detail?.product_type?.gst_percentage || 0);
    const itemTotal = parseFloat(item.total_price || 0);
    return sum + ((itemTotal * rate) / 100);
  }, 0);

  // Grand Total = Item Subtotal + GST + Delivery
  const finalAmount = totalPrice + totalGstAmount + delivery;

  const getDiscountPercent = (price, finalPrice) => {
    if (!price || !finalPrice) return 0;
    return Math.round(((price - finalPrice) / price) * 100);
  };

  const handleProceedToCheckout = () => {
    if (displayItems.length === 0) return;
    if (onClose) onClose(); 
    navigate('/checkout');  
  };

  return (
    <div className="min-h-screen bg-[#F2F1EC] py-6 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Top Header Panel */}
        <div className="p-6 bg-[#FDFDFB] border border-[#E1DFD7] rounded-md shadow-xs flex justify-between items-center">
          <div>
            <h1 className="text-[#2C2A27] text-sm uppercase tracking-widest font-semibold text-[#9A7B56]">
              My Cart ({displayItems.length})
            </h1>
            <p className="text-xs text-[#8A867E] font-light mt-0.5">Review items added to your cart</p>
          </div>
          <button onClick={onClose} className="text-[#8A867E] hover:text-[#2C2A27] text-base transition-colors p-2 rounded-md hover:bg-[#F2F1EC]">✕</button>
        </div>

        {loading ? (
          <div className="text-center py-32 text-[#A6A29A] uppercase tracking-widest text-[11px] font-semibold bg-[#FDFDFB] border border-[#E1DFD7] rounded-md shadow-xs">
            Loading cart...
          </div>
        ) : displayItems.length === 0 ? (
          <div className="text-center py-16 bg-[#FDFDFB] border border-[#E1DFD7] rounded-md max-w-sm mx-auto p-6 space-y-3 shadow-xs">
            <p className="text-[#A6A29A] text-xs font-light uppercase tracking-wider">Your cart is empty</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Left Items Column */}
            <div className="lg:col-span-2 bg-[#FDFDFB] p-6 rounded-md border border-[#E1DFD7] shadow-xs">
              <ul className="divide-y divide-[#E1DFD7]">
                {displayItems.map((item, idx) => {
                  const gstRate = parseFloat(item.product_detail?.gst_rate || item.product_detail?.product_type?.gst_percentage || 0);

                  return (
                    <li key={idx} className="flex flex-col sm:flex-row items-start py-6 first:pt-0 last:pb-0">
                      <img
                        src={item.product_detail?.photo || "https://via.placeholder.com/150"}
                        alt={item.product_detail?.product_name || "Product"}
                        className="w-28 h-28 object-cover rounded-md border border-[#E1DFD7] mr-6 mb-4 sm:mb-0 bg-[#F2F1EC]"
                      />
                      <div className="flex-grow space-y-4">
                        <div>
                          <h2 className="font-medium text-xs uppercase tracking-wide text-[#2C2A27] leading-relaxed">
                            {item.product_detail?.product_name || "Product"}
                          </h2>
                          
                          {/* 🌟 Item-level GST Indicator Tag */}
                          {gstRate > 0 ? (
                            <span className="inline-block mt-1 text-[9px] font-semibold text-[#8A867E] bg-[#F2F1EC] px-1.5 py-0.5 rounded-sm uppercase tracking-wider">
                              + {gstRate}% GST
                            </span>
                          ) : (
                            <span className="inline-block mt-1 text-[9px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm uppercase tracking-wider">
                              GST Exempt
                            </span>
                          )}
                        </div>
                        
                        {/* Quantity controls and removal option */}
                        <div className="flex items-center gap-4">
                          <div className="flex items-center border border-[#E1DFD7] bg-[#F2F1EC] rounded-sm h-8">
                            <button onClick={() => handleDecrease(item)} className="px-3 h-full text-sm text-[#5A5750] hover:text-[#9A7B56] transition-colors">-</button>
                            <input className="w-8 text-center bg-transparent text-[#2C2A27] focus:outline-none text-[11px] font-semibold" type="text" readOnly value={item.quantity || 1} />
                            <button onClick={() => handleIncrease(item)} className="px-3 h-full text-sm text-[#2C2A27] hover:text-[#9A7B56] transition-colors">+</button>
                          </div>
                          <button onClick={() => handleRemove(item)} className="text-[10px] font-semibold tracking-widest text-[#A6A29A] hover:text-red-700 transition-colors uppercase">
                            Remove
                          </button>
                        </div>
                      </div>

                      {/* Right Item Prices block */}
                      <div className="mt-4 sm:mt-0 sm:ml-6 text-left sm:text-right shrink-0">
                        <p className="text-sm font-semibold text-[#2C2A27]">₹{item.total_price}</p>
                        {item.product_detail?.price && item.product_detail?.final_price &&
                          item.product_detail.price > item.product_detail.final_price && (
                            <div className="flex sm:justify-end items-center gap-2 mt-1.5">
                              <p className="text-[11px] text-[#A6A29A] line-through font-light">
                                ₹{(item.product_detail.price * (item.quantity || 1)).toFixed(2)}
                              </p>
                              <p className="text-[9px] font-bold text-[#9A7B56] bg-[#9A7B56]/10 px-1.5 py-0.5 rounded-sm uppercase tracking-wider">
                                {getDiscountPercent(item.product_detail.price, item.product_detail.final_price)}% Off
                              </p>
                            </div>
                          )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Right Summary Checkout Sticky Block */}
            <div className="lg:col-span-1">
              <div className="bg-[#FDFDFB] p-6 rounded-md border border-[#E1DFD7] shadow-xs sticky top-24 space-y-4">
                <h2 className="text-[10px] font-bold tracking-widest text-[#A6A29A] uppercase border-b border-[#E1DFD7] pb-3">
                  Price Details
                </h2>
                
                <div className="space-y-3.5 text-xs text-[#5A5750]">
                  <div className="flex justify-between">
                    <span className="font-light">Price ({displayItems.length} units)</span>
                    <span className="text-[#2C2A27] font-medium">₹{totalOriginalPrice.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="font-light">Discount</span>
                    <span className="text-emerald-700 font-medium flex items-center gap-2">
                      {totalDiscountPercent > 0 && (
                        <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm uppercase tracking-wider">
                          {totalDiscountPercent}% Off
                        </span>
                      )}
                      -₹{totalDiscountAmount.toFixed(2)}
                    </span>
                  </div>

                  {/* 🌟 NEW: Dynamic GST Row in Price Summary */}
                  <div className="flex justify-between">
                    <span className="font-light">Estimated GST</span>
                    <span className="text-[#2C2A27] font-medium">₹{totalGstAmount.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="font-light">Delivery Charges</span>
                    <span className={`font-medium ${delivery === 0 ? "text-[10px] uppercase tracking-wider text-[#8A867E]" : "text-[#2C2A27]"}`}>
                      {delivery === 0 ? shippingMessage : `₹${delivery.toFixed(2)}`}
                    </span>
                  </div>
                  
                  <div className="border-t border-dashed border-[#E1DFD7] my-2"></div>
                  
                  <div className="flex justify-between text-sm font-semibold text-[#2C2A27] pt-1">
                    <span>Total Amount</span>
                    <span className="text-base font-bold">₹{finalAmount.toFixed(2)}</span>
                  </div>
                </div>

                <button 
                  onClick={handleProceedToCheckout} 
                  className="w-full bg-[#F2F1EC] hover:bg-[#9A7B56] text-[#5A5750] hover:text-white border border-[#E1DFD7] font-semibold text-[11px] tracking-widest uppercase py-3 rounded-sm transition-all duration-300 mt-2"
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
            
          </div>
        )}
      </div>
    </div>
  );
}

export default Cart;